/**
 * AssumedInputsPanel — the calculation engine's declared assumptions for a
 * PDF-ingested company. Shown on the metrics AND sensitivity screens.
 *
 * PDF companies have no live market data, so some engine inputs are assumed
 * (e.g. book-value WACC weights, official-source risk-free rate, measured tax
 * rate). The engine writes that provenance onto the CalculatedMetrics node as
 * Assumed_Inputs (India, PascalCase) / assumed_inputs (US, snake_case) — an
 * array of self-describing strings, rendered VERBATIM here, never re-worded.
 * Renders nothing when no year carries assumptions (i.e. every non-PDF
 * company), so callers can mount it unconditionally.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Card } from '../../ui/Card';
import {
  resolveAssumedInputs,
  resolveCalcEngine,
  resolveWaccMethod,
} from '../utils/extendedFields';
import type { UnifiedFinancialRecord } from '../../../types/financial';

interface Props {
  /** Records sorted ascending by year (the hook's guarantee). */
  data: UnifiedFinancialRecord[];
}

export default function AssumedInputsPanel({ data }: Props) {
  const [expanded, setExpanded] = useState(true);

  // The assumptions describe the engine run, not a single fiscal year — take
  // them from the most recent year that carries any.
  const latestWithInputs = [...data].reverse().find((d) => resolveAssumedInputs(d).length > 0);
  const inputs = latestWithInputs ? resolveAssumedInputs(latestWithInputs) : [];
  const engine = latestWithInputs ? resolveCalcEngine(latestWithInputs) : null;
  const waccMethod = latestWithInputs ? resolveWaccMethod(latestWithInputs) : null;

  if (inputs.length === 0) return null;

  return (
    <Card className="border-blue-100">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-blue-50/40 transition-colors"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Info className="w-4 h-4 text-blue-500 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-neutral-800">Assumptions</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              How the calculation engine sourced or substituted inputs for these PDF-extracted statements
            </p>
          </div>
        </div>
        <span className="flex items-center gap-2 shrink-0">
          <span className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-[11px] font-medium text-blue-700">
            {inputs.length}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-400" />
          )}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-neutral-100">
          <ul className="pt-3 space-y-2">
            {inputs.map((line, i) => (
              <li key={i} className="flex items-start gap-2.5 text-xs text-neutral-700 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                <span className="whitespace-pre-line">{line}</span>
              </li>
            ))}
          </ul>
          {(engine || waccMethod) && (
            <p className="mt-3 pt-2 border-t border-neutral-100 text-[11px] text-neutral-400">
              {engine && (
                <>Calculation engine: <span className="font-mono">{engine}</span></>
              )}
              {engine && waccMethod && ' · '}
              {waccMethod && (
                <>WACC method: <span className="font-mono">{waccMethod}</span></>
              )}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
